import * as PropTypes from 'prop-types';
import _ from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import UserConstants, {BaseEntity, User} from 'constants/UserConstants';
import {Institution} from 'institutions/InstitutionConstants';

const {USER_SHAPE} = UserConstants;

export enum Plan {
  ELN_PRO = 'eln_pro',
  SCHOLAR = 'scholar',
}

export interface Organization extends BaseEntity {
  admin: boolean;
  allowNonadminsToSetOrgToOwnableOwner: boolean;
  autoEnablePlan: Plan | null;
  autoProvisionedCapabilities: [];
  avatar_url: string;
  disablePersonalOwnables: boolean;
  disablePublic: boolean;
  defaultOwnableMemberDataPolicyId?: string;
  email: string;
  enableAdminView: boolean;
  institution: Institution | null;
  joinedOn: string;
  location?: string;
  member: boolean;
  type: ModelTypeConstants.ORGANIZATION;
  url_identifier: string;
  urlIdentifier: string;
  website?: string;
  permissions?: {
    manageRoles: boolean;
  };
}

export interface OrganizationWithInitials extends Organization {
  initials?: string;
}

export const ORGANIZATION = PropTypes.shape({
  admin: PropTypes.bool.isRequired,
  avatar_url: PropTypes.string.isRequired,
  disablePersonalOwnables: PropTypes.bool.isRequired,
  defaultOwnableMemberDataPolicyId: PropTypes.string,
  disablePublic: PropTypes.bool.isRequired,
  email: PropTypes.string.isRequired,
  handle: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  location: PropTypes.string,
  member: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  url_identifier: PropTypes.string.isRequired,
  website: PropTypes.string,
});

export interface Team {
  admin: boolean;
  autoProvisionedCapabilities: [];
  handle: string;
  id: string;
  member: boolean;
  name: string;
  isSchemaAdmin: boolean;
  avatar_url: string;
  description: string;
  organizationId: string;
  memberCount: number;
  type: ModelTypeConstants.TEAM;
}

export enum OrganizationPermissionRole {
  MEMBER = 1,
  ADMIN = 0,
}

export enum TeamPermissionRole {
  MEMBER = 1,
  ADMIN = 0,
}

export interface OrganizationRequest {
  createdAt: string;
  inviter: User;
  organization: Organization;
  organizationAccepted: boolean;
  user: User;
  userAccepted: boolean;
}

export interface UserOrganizationRequestsResponse {
  currentUserInboundRequests: Array<OrganizationRequest>;
  currentUserOutboundRequests: Array<OrganizationRequest>;
  pendingInvites: number;
  userOrgsInboundRequests: Array<OrganizationRequest>;
}

export const ORGANIZATION_REQUEST = PropTypes.shape({
  createdAt: PropTypes.string.isRequired,
  inviter: PropTypes.shape(USER_SHAPE).isRequired,
  organization: ORGANIZATION,
  organizationAccepted: PropTypes.bool.isRequired,
  user: PropTypes.shape(USER_SHAPE).isRequired,
  userAccepted: PropTypes.bool.isRequired,
});

export interface Member {
  id: string;
  name: string;
  email?: string;
  avatar_url: string;
  handle: string;
  initials?: string;
  level: OrganizationPermissionRole;
  type: ModelTypeConstants.USER | ModelTypeConstants.BENCHLING_APP | ModelTypeConstants.ROUTINE;
}

export const MEMBER = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  email: PropTypes.string,
  avatar_url: PropTypes.string.isRequired,
  handle: PropTypes.string.isRequired,
});

export interface MemberInfo {
  lastSeen: string | null;
  loading: boolean;
}

export interface MemberInvite {
  email: string;
  createdAt: string;
}

export const MEMBER_INVITE = PropTypes.shape({
  email: PropTypes.string.isRequired,
  createdAt: PropTypes.string.isRequired,
});

export interface MemberRequest {
  user: {
    id: string;
    name: string;
    email: string;
    handle: string;
  };
  createdAt: string;
  userAccepted: boolean;
}

export const MEMBER_REQUEST = PropTypes.shape({
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    handle: PropTypes.string.isRequired,
  }).isRequired,
  createdAt: PropTypes.string.isRequired,
  userAccepted: PropTypes.bool.isRequired,
});

export const TEAM = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  avatar_url: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  organizationId: PropTypes.string.isRequired,
  memberCount: PropTypes.number.isRequired,
});

export const CREATE_ORGANIZATION_SCREEN = 'CREATE_ORGANIZATION_SCREEN';
export const SHARE_PROJECTS_SCREEN = 'SHARE_PROJECTS_SCREEN';
export const ORGANIZATION_SCREEN = 'ORGANIZATION_SCREEN';
export const TEAM_SCREEN = 'TEAM_SCREEN';
export const USER_SCREEN = 'USER_SCREEN';
export const CREATE_TEAM_SCREEN = 'CREATE_TEAM_SCREEN';

export const ALL_SCREENS = [
  CREATE_ORGANIZATION_SCREEN,
  CREATE_TEAM_SCREEN,
  SHARE_PROJECTS_SCREEN,
  ORGANIZATION_SCREEN,
  TEAM_SCREEN,
  USER_SCREEN,
] as const;

export type OrganizationScreen = (typeof ALL_SCREENS)[number];

export const SEEN_FEATURE_BANNER_PREFERENCE = 'organizations.seen_feature_banner';

export const PROJECTS_TAB = 'PROJECTS_TAB';
export const CALENDAR_TAB = 'CALENDAR_TAB';
export const MEMBERS_TAB = 'MEMBERS_TAB';
export const SETTINGS_TAB = 'SETTINGS_TAB';
export const TEAMS_TAB = 'TEAMS_TAB';
export const APPS_TAB = 'APPS_TAB';
export const AGENTS_TAB = 'AGENTS_TAB';
export const ACCESS_TAB = 'ACCESS_TAB';
export const ROLES_TAB = 'ROLES_TAB';

export const ALL_TABS = [
  PROJECTS_TAB,
  CALENDAR_TAB,
  MEMBERS_TAB,
  SETTINGS_TAB,
  TEAMS_TAB,
  APPS_TAB,
  AGENTS_TAB,
  ACCESS_TAB,
  ROLES_TAB,
];

// Keep in sync with `benchling/start/views.py:638`
export const ORGANIZATION_PAGE_SECTION_URL_PARAM_TO_TAB = {
  members: MEMBERS_TAB,
  projects: PROJECTS_TAB,
  calendar: CALENDAR_TAB,
  settings: SETTINGS_TAB,
  teams: TEAMS_TAB,
  apps: APPS_TAB,
  agents: AGENTS_TAB,
};
// Keep in sync with `benchling/start/views.py:team_page`
export const TEAM_PAGE_SECTION_URL_PARAM_TO_TAB = {
  calendar: CALENDAR_TAB,
  members: MEMBERS_TAB,
  projects: PROJECTS_TAB,
  settings: SETTINGS_TAB,
  apps: APPS_TAB,
  agents: AGENTS_TAB,
};
// Keep in sync with `benchling/start/views.py:user_page`
export const USER_PAGE_SECTION_URL_PARAM_TO_TAB = {
  settings: SETTINGS_TAB,
  roles: ROLES_TAB,
  access: ACCESS_TAB,
};

export const ORGANIZATION_PAGE_TAB_TO_URL_PARAM = Object.entries(
  ORGANIZATION_PAGE_SECTION_URL_PARAM_TO_TAB
).reduce<Record<string, string>>((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {});
export const TEAM_PAGE_TAB_TO_URL_PARAM = _.invert(TEAM_PAGE_SECTION_URL_PARAM_TO_TAB);
export const USER_PAGE_TAB_TO_URL_PARAM = _.invert(USER_PAGE_SECTION_URL_PARAM_TO_TAB);

export type OrganizationPageTab = keyof typeof ORGANIZATION_PAGE_TAB_TO_URL_PARAM;

export interface OrganizationPermission {
  userId: string;
  organizationId: string;
  initials: string;
  level: OrganizationPermissionRole | null;
}

export interface OrganizationInvite {
  id: number;
  createdAt: string;
  email: string;
  organization_id: number;
}
